import { motion } from 'framer-motion';
import React from 'react';
import { FiCloud, FiGlobe, FiLayers, FiServer, FiShield } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const CloudInfrastructureSection = () => {
  const cloudServices = [
    {
      icon: FiCloud,
      title: "Cloud Migration",
      description: "Seamless transition to cloud platforms with minimal downtime",
      features: ["AWS", "Azure", "Google Cloud"]
    },
    {
      icon: FiServer,
      title: "Infrastructure Design",
      description: "Scalable and resilient infrastructure architecture",
      features: ["Auto-scaling", "Load Balancing", "High Availability"]
    },
    {
      icon: FiShield,
      title: "Security & Compliance",
      description: "Enterprise-grade security with compliance standards",
      features: ["SOC 2", "GDPR", "HIPAA"]
    }
  ];

  return (
    <section id="cloud-infrastructure" className="py-20 bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-blue-200/40 to-transparent rounded-full"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-r from-indigo-200/40 to-transparent rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide"
          >
            <FiCloud className="animate-bounce" />
            <span>Scalable Solutions</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight"
          >
            Cloud &
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
              Infrastructure
            </span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-body font-light tracking-wide"
          >
            Build robust, scalable, and secure cloud infrastructure that grows with your business. 
            Our expert team ensures optimal performance, cost efficiency, and seamless operations.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {cloudServices.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ 
                y: -10,
                rotateX: 5,
                transition: { duration: 0.3 }
              }}
              className="group perspective-1000"
            >
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-100 relative overflow-hidden">
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <service.icon className="text-white" size={28} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6 font-body">{service.description}</p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.2 + idx * 0.1 + 0.5 }}
                        viewport={{ once: true }}
                        className="flex items-center space-x-2"
                      >
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-sm text-gray-700 font-medium font-body tracking-wide">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Architecture Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-xl mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">Cloud Architecture Excellence</h3>
            <p className="text-gray-600 text-lg font-body font-light tracking-wide">Modern, scalable, and secure infrastructure design</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Frontend Layer */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <FiGlobe className="text-white" size={32} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 font-heading tracking-wide">Frontend Layer</h4>
              <p className="text-sm text-gray-600 font-body">CDN, Load Balancers, Web Servers</p>
            </motion.div>

            {/* Application Layer */}
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <FiLayers className="text-white" size={32} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 font-heading tracking-wide">Application Layer</h4>
              <p className="text-sm text-gray-600 font-body">Microservices, APIs, Containers</p>
            </motion.div>

            {/* Data Layer */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <FiServer className="text-white" size={32} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 font-heading tracking-wide">Data Layer</h4>
              <p className="text-sm text-gray-600 font-body">Databases, Storage, Analytics</p>
            </motion.div>
          </div>

          {/* Connection Lines */}
          <div className="hidden md:block relative mt-8">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 1 }}
              viewport={{ once: true }}
              className="absolute top-1/2 left-1/3 w-1/3 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 origin-left"
            />
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1, duration: 1 }}
              viewport={{ once: true }}
              className="absolute top-1/2 right-1/3 w-1/3 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 origin-right"
            />
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
          >
            Design Your Infrastructure
          </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CloudInfrastructureSection; 